import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Select from 'react-select';
import api from '../services/apiService';
import { usePerms } from '../hooks/useAuth';
import HpExportStatus from './HpExportStatus';

const filterFields = [
  { key: 'name', optionsKey: 'names', label: 'Name' },
  { key: 'productGroup', optionsKey: 'productGroups', label: 'Produktgruppe' },
  { key: 'motorType', optionsKey: 'motorTypes', label: 'Motortyp' },
  { key: 'machineType', optionsKey: 'machineTypes', label: 'Maschinentyp' },
  { key: 'itemType', optionsKey: 'itemTypes', label: 'Artikelart' },
  { key: 'deactivated', optionsKey: 'deactivated', label: 'Deaktiviert' },
  { key: 'foreignArticle', optionsKey: 'foreignArticle', label: 'Fremdartikel' },
  { key: 'hpActive', label: 'HP aktiv' },
];
const yesNoOptions = [{ value: true, label: 'Ja' }, { value: false, label: 'Nein' }];
const hpActiveOptions = [
  { value: true, label: 'HP aktiv' },
  { value: false, label: 'HP nicht aktiv' },
];
const paginationButtonStyle = {
  fontSize: '15px',
  padding: '8px 12px',
  backgroundColor: 'rgb(155, 155, 155)',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
};

export default function ItemWebChannelHpList() {
  const { has } = usePerms();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState(() => Object.fromEntries(filterFields.map(({ key }) => [key, null])));
  const [serverFilterOptions, setServerFilterOptions] = useState({
    names: [],
    productGroups: [],
    motorTypes: [],
    machineTypes: [],
    itemTypes: [],
    deactivated: yesNoOptions,
    foreignArticle: yesNoOptions,
  });
  const [filterError, setFilterError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const response = await api.get('/items/filter-options');
        if (live) {
          setServerFilterOptions(previous => ({ ...previous, ...response.data }));
          setFilterError('');
        }
      } catch (err) {
        if (live) setFilterError('Die Filteroptionen konnten nicht geladen werden. Bitte den Status aktualisieren.');
      }
    };
    load();
    return () => { live = false; };
  }, [refresh]);

  const query = useMemo(() => {
    const params = new URLSearchParams({ limit: 25, page });
    filterFields.forEach(({ key }) => {
      // Wie bei LO wird der Kanalstatus auf der aktuellen Seite gefiltert.
      if (key !== 'hpActive' && filters[key] !== null) {
        params.set(key, filters[key].value);
      }
    });
    return params.toString();
  }, [page, filters]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    const load = async () => {
      try {
        const response = await api.get(`/items/list?${query}`);
        const data = response.data.data || response.data;
        const statuses = await Promise.all(data.map(async item => {
          const channels = await api.get(`/item-web-channels/item/${item.id}`);
          return [item.id, !!(channels.data || []).find(channel => channel.channelKey === 'HP')?.isActive];
        }));
        if (live) {
          setItems(data);
          setPages(response.data.pages || 1);
          setActive(Object.fromEntries(statuses));
        }
      } catch (err) {
        if (live) setError(err.response?.data?.error || 'HP-Liste konnte nicht geladen werden.');
      } finally {
        if (live) setLoading(false);
      }
    };
    load();
    return () => { live = false; };
  }, [query, refresh]);

  const itemsToRender = useMemo(() => (
    filters.hpActive === null ? items : items.filter(item => active[item.id] === filters.hpActive.value)
  ), [items, active, filters.hpActive]);

  const changeFilter = (key, selected) => {
    setFilters(previous => ({ ...previous, [key]: selected }));
    setPage(1);
  };

  return (
    <div>
      <Helmet>
        <title>Channel: Homepage</title>
        <meta name="description" content="HP WebChannel Konfiguration" />
      </Helmet>
      <h2>Homepage – Veröffentlichungen</h2>
      <p>
        {has('/hp-integration') && (
          <><Link to="/hp-integration">HP-Verbindung und Exportstatus</Link>{' · '}</>
        )}
        <button type="button" disabled={loading} onClick={() => setRefresh(current => current + 1)}>
          Status aktualisieren
        </button>
      </p>
      <HpExportStatus refresh={refresh} />

      <div style={{ marginBottom: '20px' }}>
        <h3>Artikel filtern</h3>
        <div className="filter-grid">
          {filterFields.map(({ key, optionsKey, label }) => (
            <Select
              key={key}
              inputId={`hp-filter-${key}`}
              aria-label={label}
              options={optionsKey
                ? (serverFilterOptions[optionsKey] || []).map(option => (
                  typeof option === 'string' ? { value: option, label: option } : option
                ))
                : hpActiveOptions}
              placeholder={label}
              isClearable
              value={filters[key]}
              onChange={selected => changeFilter(key, selected)}
            />
          ))}
        </div>
        {filters.hpActive !== null && <p>Der Filter „HP aktiv“ bezieht sich auf die aktuelle Seite.</p>}
      </div>

      {filterError && <p role="alert">{filterError}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>Lade HP Web-Channel Liste...</p> : !error && <>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Arbeitshöhe</th>
              <th>Produktgruppe</th>
              <th>Motortyp</th>
              <th>Maschinentyp</th>
              <th>Artikelart</th>
              <th>HP aktiv</th>
              <th>HP-Veröffentlichung</th>
              <th>HP bearbeiten</th>
            </tr>
          </thead>
          <tbody>
            {itemsToRender.map(item => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.name}</td>
                <td>{item.maxWorkingHeight != null && item.maxWorkingHeight !== '' ? `${item.maxWorkingHeight} m` : '-'}</td>
                <td>{item.productGroup?.name || '-'}</td>
                <td>{item.motorType?.name || '-'}</td>
                <td>{item.machineType?.name || '-'}</td>
                <td>{item.itemType || '-'}</td>
                <td>{active[item.id] ? 'Ja' : 'Nein'}</td>
                <td>{active[item.id] ? item.deactivated ? 'Artikel deaktiviert' : 'Freigegeben' : 'Nicht freigegeben'}</td>
                <td>
                  {has('/itemWebChannelHP/:itemId/edit') && (
                    <Link to={`/itemWebChannelHP/${item.id}/edit`}>bearbeiten</Link>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!itemsToRender.length && <p>Keine Maschinen gefunden (mit den aktuellen Filtern).</p>}
      </>}

      <div style={{ marginTop: '1rem' }}>
        <button
          type="button"
          disabled={page === 1 || loading}
          onClick={() => setPage(current => Math.max(current - 1, 1))}
          style={paginationButtonStyle}
        >
          Vorherige
        </button>
        <span style={{ margin: '0 1rem' }}>Seite {page} von {pages}</span>
        <button
          type="button"
          disabled={page >= pages || loading || !!error}
          onClick={() => setPage(current => current + 1)}
          style={paginationButtonStyle}
        >
          Nächste
        </button>
      </div>
    </div>
  );
}
